import { useState, useEffect } from "react";
import api from "../../services/api";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import FieldDefinition from "../dynamicsFields/FieldDefinition";
import { TIPOS_DATO } from "../dynamicsFields/fieldTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareMinus, faSquarePlus, faPencil } from "@fortawesome/free-solid-svg-icons";
import EditModal from "../ui/EditModal.js";

const AddVacunasPorPatologia = () => {
  const [Patologias, setPatologias] = useState([]);
  const [Vacunas, setVacunas] = useState([]);
  const [IdPatologia, setIdPatologia] = useState("");
  const [IdVacuna, setIdVacuna] = useState("");
  const [VacunasPorPatologia, setVacunasPorPatologia] = useState([]);
  const [fieldDefinition, setFieldDefinition] = useState({});
  const [filtroEstado, setFiltroEstado] = useState("activas");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [vacunaAEditar, setVacunaAEditar] = useState(null);

  useEffect(() => {
    getPatologias();
    getVacunas();
  }, []);

  useEffect(() => {
    if (IdPatologia) {
      setIdVacuna("");
      setFieldDefinition({});
      cargarVacunasPorPatologia(IdPatologia);
    }
  }, [IdPatologia]);

  const getPatologias = async () => {
    try {
      const res = await api.get("/patologias");
      setPatologias(res.data.filter(p => p.estado === 1));
    } catch (error) {
      console.error("Error al obtener patologías:", error);
    }
  };

  const getVacunas = async () => {
    try {
      const res = await api.get("/vacunas");
      setVacunas(res.data.filter(v => v.Estado === 1));
    } catch (error) {
      console.error("Error al obtener vacunas:", error);
    }
  };

  const cargarVacunasPorPatologia = async (idPatologia) => {
    try {
      const res = await api.get("/vacunas-por-patologia");
      const filtradas = res.data
        .filter(vp => vp.IdPatologia === idPatologia)
        .map(vp => ({
          ...vp,
          NombreVacuna:
            Vacunas.find(v => v.IdVacunacion === vp.IdVacunacion)?.Nombre ||
            "Desconocida",
          json_definicion:
            typeof vp.json_definicion === "string"
              ? (() => { try { return JSON.parse(vp.json_definicion); } catch { return {}; } })()
              : vp.json_definicion
        }));
      setVacunasPorPatologia(filtradas);
    } catch (error) {
      console.error("Error al obtener vacunas por patología:", error);
    }
  };

  const guardarVacunaPorPatologia = async (e) => {
    e.preventDefault();

    if (!IdPatologia || !IdVacuna) {
      return Swal.fire("Campos incompletos", "Debe seleccionar patología y vacuna", "warning");
    }

    if (!fieldDefinition.tipo) {
      return Swal.fire("Configuración incompleta", "Debe configurar los campos", "warning");
    }

    if ([TIPOS_DATO.SELECCION].includes(fieldDefinition.tipo) &&
        (!fieldDefinition.configuracion?.opciones?.length)) {
      return Swal.fire("Configuración incompleta", "Debe agregar opciones", "warning");
    }

    // Validar que no exista duplicado (sin importar estado)
    const yaExiste = VacunasPorPatologia.some(
      vp => vp.IdVacunacion === IdVacuna && vp.IdPatologia === IdPatologia
    );

    if (yaExiste) {
      return Swal.fire({
        icon: "warning",
        title: "Vacuna duplicada",
        text: "Esta vacuna ya está asociada a la patología seleccionada.",
      });
    }

    try {
      await api.post("/vacunas-por-patologia", {
        IdVacunacion: IdVacuna,
        IdPatologia,
        json_definicion: fieldDefinition,
      });

      Swal.fire("Éxito", "Vacuna asociada a la patología", "success");
      cargarVacunasPorPatologia(IdPatologia);
      setIdVacuna("");
      setFieldDefinition({});
    } catch (error) {
      console.error("Error al guardar relación:", error);
      Swal.fire("Error", "No se pudo asociar la vacuna", "error");
    }
  };

  const toggleEstado = async (id) => {
    try {
      await api.patch(`/vacunas-por-patologia/${id}/estado`);
      cargarVacunasPorPatologia(IdPatologia);
      Swal.fire("Actualizado", "Estado actualizado correctamente", "success");
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      Swal.fire("Error", "No se pudo cambiar el estado", "error");
    }
  };

  const actualizarVacuna = async (valores) => {
    try {
      // Validar que el tipo de dato esté configurado
      if (!valores.json_definicion || !valores.json_definicion.tipo) {
        Swal.fire("Configuración incompleta", "Debe configurar el tipo de dato para la vacuna.", "warning");
        return;
      }

      // Si es de tipo selección, validar que tenga opciones
      if (
        [TIPOS_DATO.SELECCION, TIPOS_DATO.MULTIPLE].includes(valores.json_definicion.tipo) &&
        (!valores.json_definicion.configuracion?.opciones || valores.json_definicion.configuracion.opciones.length === 0)
      ) {
        Swal.fire("Configuración incompleta", "Para tipos de selección, debe agregar al menos una opción.", "warning");
        return;
      }

      // Actualizar la vacuna (nombre y descripción)
      await api.put(`/vacunas/${valores.IdVacunacion}`, {
        Nombre: valores.Nombre,
        Descripcion: valores.Descripcion || "",
      });

      // Actualizar la relación vacuna-patología con el nuevo json_definicion
      if (IdPatologia) {
        await api.put(`/vacunas-por-patologia/actualizar-json-definicion`, {
          IdPatologia: parseInt(IdPatologia),
          IdVacunacion: valores.IdVacunacion,
          json_definicion: valores.json_definicion
        });
      }

      Swal.fire("Actualizado", "La vacuna fue actualizada correctamente.", "success");
      setEditModalOpen(false);
      cargarVacunasPorPatologia(IdPatologia);
      getVacunas();
    } catch (error) {
      console.error("Error al actualizar vacuna:", error);
      Swal.fire("Error", "No se pudo actualizar la vacuna.", "error");
    }
  };

  const columnas = [
    {
      name: "Vacuna",
      selector: row => row.NombreVacuna || "Desconocida",
      sortable: true,
      width: "40%",
    },
    {
      name: "Tipo de campo",
      selector: row => row.json_definicion?.tipo || "No configurado",
      width: "30%",
    },
    {
      name: "Acciones",
      cell: row => (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            className="button is-small is-info"
            title="Editar"
            onClick={() => {
              const vacOriginal = Vacunas.find(v => v.IdVacunacion === row.IdVacunacion);
              setVacunaAEditar({
                IdVacunacion: row.IdVacunacion,
                IdVacunacionPorPatologia: row.IdVacunacionPorPatologia,
                Nombre: vacOriginal?.Nombre || row.NombreVacuna || "",
                Descripcion: vacOriginal?.Descripcion || "",
                json_definicion: row.json_definicion || { tipo: "", configuracion: {} },
              });
              setEditModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faPencil} />
          </button>
          {row.estado === 1 ? (
            <FontAwesomeIcon
              icon={faSquarePlus}
              style={{ color: "#37ff00", cursor: "pointer" }}
              size="2xl"
              title="Desactivar"
              onClick={() => toggleEstado(row.IdVacunacionPorPatologia)}
            />
          ) : (
            <FontAwesomeIcon
              icon={faSquareMinus}
              style={{ color: "#ff0000", cursor: "pointer" }}
              size="2xl"
              title="Activar"
              onClick={() => toggleEstado(row.IdVacunacionPorPatologia)}
            />
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "30%",
      center: true,
    },
  ];

  const filtrarPorEstado = () => {
    if (filtroEstado === "todas") return VacunasPorPatologia;
    if (filtroEstado === "activas") return VacunasPorPatologia.filter(v => v.estado === 1);
    if (filtroEstado === "inactivas") return VacunasPorPatologia.filter(v => v.estado === 0);
    return VacunasPorPatologia;
  };

  return (
    <div className="columns is-centered">
      <div className="column is-half">
        <div className="notification is-primary">
          <strong>Vacunas por Patología</strong>
        </div>

        <form onSubmit={guardarVacunaPorPatologia}>
          <div className="field">
            <label className="label">Patología</label>
            <div className="select is-fullwidth">
              <select
                value={IdPatologia}
                onChange={e => setIdPatologia(parseInt(e.target.value))}
              >
                <option value="">Seleccionar</option>
                {Patologias.map(p => (
                  <option key={p.IdPatologia} value={p.IdPatologia}>
                    {p.Nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label className="label">Vacuna</label>
            <div className="select is-fullwidth">
              <select
                value={IdVacuna}
                onChange={e => setIdVacuna(parseInt(e.target.value))}
              >
                <option value="">Seleccionar</option>
                {Vacunas.map(v => (
                  <option key={v.IdVacunacion} value={v.IdVacunacion}>
                    {v.Nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {IdPatologia && IdVacuna && (
            <FieldDefinition
              inicial={fieldDefinition}
              onChange={setFieldDefinition}
            />
          )}

          <button className="button is-success mt-4">Guardar</button>
        </form>

        <div style={{ display: "flex", gap: "10px", margin: "1rem 0" }}>
          {["todas", "activas", "inactivas"].map(estado => (
            <button
              key={estado}
              type="button"
              onClick={() => setFiltroEstado(estado)}
              className={`button is-small ${filtroEstado===estado?'is-primary':'is-light'}`}
            >
              {estado.charAt(0).toUpperCase() + estado.slice(1)}
            </button>
          ))}
        </div>

        <DataTable
          columns={columnas}
          data={filtrarPorEstado()}
          pagination
          noDataComponent="No hay vacunas configuradas"
        />

        {editModalOpen && (
          <EditModal
            title="Editar Vacuna"
            initialValues={vacunaAEditar}
            fields={[
              { name: "Nombre", label: "Nombre", type: "text", required: true },
              { name: "Descripcion", label: "Descripción", type: "textarea", required: false },
              {
                name: "json_definicion",
                label: "Configuración del campo",
                type: "custom",
                required: true,
                component: FieldDefinition,
                componentProps: {
                  allSymptoms: Vacunas,
                  sintomasPorPatologia: VacunasPorPatologia,
                },
              },
            ]}
            onSubmit={actualizarVacuna}
            onClose={() => setEditModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AddVacunasPorPatologia;
